import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, take, finalize, catchError } from 'rxjs/operators';

import { LicenceService } from './request/licence.service';
import { UserGroup } from 'src/app/models/user-group.model';
import { IdentityService } from './request/identity.service';
import { User } from 'src/app/models/user.model';
import { UserDetailData } from 'src/app/models/user-detail-data.model';
import { AuthorizationService } from './request/auth.service';
import { LicenceGroup } from 'src/app/models/licence-group.model';
import { LicenceGroupService } from './request/licence-group.service';
import { UserGroupService } from './request/user-group.service';
import { UserService } from './request/user.service';
import { RoleService } from './request/role.service';
import { Role } from 'src/app/models/role.model';
import { Provider } from 'src/app/models/provider.model';
import { ActiveDirectoryService } from './request/active-directory.service';
import { Licence } from 'src/app/models/licence.model';
import { FileInput } from 'ngx-material-file-input';
import { Configuration, ConfigurationService, ConfigurationVersion } from './request/configuration.service';
import { ConfigurationSummary } from 'src/app/models/configuration-summary.model';

@Injectable({
  providedIn: 'root'
})


// TODO: this is a move away from having to manage the data and state all over the place
// it is not perfect and in many cases uses a hammer instead of a scalpel...to be worked on.
// At least it is in one spot right?

// Note: the observable subscriptions that come from the services are http calls
// that should auto unsubscribe when complete.
export class AdminDataService {

  private isInitializing = false;
  private configRequestInProgress = false;

  private _userGroupDetails: BehaviorSubject<UserGroup[]>;
  private _currentSelectedUserGroupDetail: BehaviorSubject<UserGroup>;
  private _licence: BehaviorSubject<Licence>;
  private _licenceGroupDetails: BehaviorSubject<LicenceGroup[]>;
  private _roles: BehaviorSubject<Role[]>;
  private _currentSelectedLicenceGroupDetail: BehaviorSubject<LicenceGroup>;
  private _currentLicenceGroupUsers: BehaviorSubject<UserDetailData[]>;
  private _currentUserGroupUsers: BehaviorSubject<UserDetailData[]>;
  private _currentLicenceUserGroups: BehaviorSubject<UserGroup[]>;
  private _userDetails: BehaviorSubject<UserDetailData[]>;
  private _currentSelectedUserDetail: BehaviorSubject<UserDetailData>;
  private _providers: BehaviorSubject<Provider[]>;
  private _currentProvider: BehaviorSubject<Provider>;
  private _currentUserLicenceGroups: BehaviorSubject<LicenceGroup[]>;
  private _currentUserGroupLicenceGroups: BehaviorSubject<LicenceGroup[]>;
  private _currentUserUserGroups: BehaviorSubject<UserGroup[]>;
  private _currentSelectedConfigurationDetails = new BehaviorSubject<ConfigurationSummary>(null);
  private _currentConfigurationVersions = new BehaviorSubject<ConfigurationVersion[]>([]);
  private _configurationSummaries = new BehaviorSubject<ConfigurationSummary[]>([]);
  private _userDeleted = new BehaviorSubject<boolean>(false);

  private dataStore: {
    userGroupDetails: UserGroup[],
    currentSelectedUserGroupDetail: UserGroup,
    licence: Licence,
    licenceGroupDetails: LicenceGroup[],
    currentSelectedLicenceGroupDetail: LicenceGroup,
    userDetails: UserDetailData[],
    groupDetails: UserGroup[],
    currentSelectedUserDetail: UserDetailData,
    currentSelectedGroupDetail: UserGroup,
    roles: Role[],
    providers: Provider[],
    currentProvider: Provider,
    currentUserGroups: UserGroup[],
    currentUserLicenceGroups: LicenceGroup[],
    currentUserGroupLicenceGroups: LicenceGroup[],
    currentUserUserGroups: UserGroup[],
    currentLicenceGroupUsers: UserDetailData[],
    currentLicenceUserGroups: UserGroup[],
    currentUserGroupUsers: UserDetailData[],
    configurationSummaries: ConfigurationSummary[],
    currentSelectedConfigurationDetail: ConfigurationSummary,
    currentConfigurationVersions: ConfigurationVersion[]
  };

  constructor(
    private authService: AuthorizationService,
    private userService: UserService,
    private licenceGroupService: LicenceGroupService,
    private userGroupService: UserGroupService,
    private roleService: RoleService,
    private licenceService: LicenceService,
    private identityService: IdentityService,
    private configurationService: ConfigurationService,
    public activeDirectoryService: ActiveDirectoryService) {
    this._userGroupDetails = <BehaviorSubject<UserGroup[]>>new BehaviorSubject([]);
    this._currentSelectedUserGroupDetail = <BehaviorSubject<UserGroup>>new BehaviorSubject(null);
    this._licence = <BehaviorSubject<Licence>>new BehaviorSubject(null);
    this._licenceGroupDetails = <BehaviorSubject<LicenceGroup[]>>new BehaviorSubject([]);
    this._roles = <BehaviorSubject<Role[]>>new BehaviorSubject([]);
    this._providers = <BehaviorSubject<Provider[]>>new BehaviorSubject([]);
    this._currentProvider = <BehaviorSubject<Provider>>new BehaviorSubject(null);
    this._currentUserUserGroups = <BehaviorSubject<UserGroup[]>>new BehaviorSubject([]);
    this._currentSelectedLicenceGroupDetail = <BehaviorSubject<LicenceGroup>>new BehaviorSubject(null);
    this._userDetails = <BehaviorSubject<UserDetailData[]>>new BehaviorSubject([]);
    this._currentSelectedUserDetail = <BehaviorSubject<UserDetailData>>new BehaviorSubject(null);
    this._currentUserLicenceGroups = <BehaviorSubject<LicenceGroup[]>>new BehaviorSubject([]);
    this._currentUserGroupLicenceGroups = <BehaviorSubject<LicenceGroup[]>>new BehaviorSubject([]);
    this._currentLicenceGroupUsers = <BehaviorSubject<UserDetailData[]>>new BehaviorSubject([]);
    this._currentLicenceUserGroups = <BehaviorSubject<UserGroup[]>>new BehaviorSubject([]);
    this._currentUserGroupUsers = <BehaviorSubject<UserDetailData[]>>new BehaviorSubject([]);
    this._currentSelectedConfigurationDetails = new BehaviorSubject<ConfigurationSummary>(null);
    this._currentConfigurationVersions = new BehaviorSubject<ConfigurationVersion[]>([]);
    this._configurationSummaries = new BehaviorSubject<ConfigurationSummary[]>([]);

    this.dataStore = {
      userGroupDetails: [],
      currentSelectedUserGroupDetail: null,
      licence: null,
      licenceGroupDetails: [],
      currentSelectedLicenceGroupDetail: null,
      userDetails: [],
      groupDetails: [],
      currentSelectedGroupDetail: null,
      currentSelectedUserDetail: null,
      roles: [],
      providers: [],
      currentProvider: null,
      currentUserGroups: [],
      currentUserLicenceGroups: [],
      currentUserGroupLicenceGroups: [],
      currentUserUserGroups: [],
      currentLicenceGroupUsers: [],
      currentLicenceUserGroups: [],
      currentUserGroupUsers: [],
      configurationSummaries: [],
      currentSelectedConfigurationDetail: null,
      currentConfigurationVersions: []
    };
  }

  initialize() {
    if (this.isInitializing) {
      console.log('Initialization already in progress, skipping duplicate call');
      return;
    }
    
    this.isInitializing = true;
    
    this.authService.hasPermission('groups:*').then(hasPermission => {
      if (hasPermission) {
        // Create a counter to track when all async operations complete
        let pendingOperations = 6; // 5 services + config
        const markComplete = () => {
          pendingOperations--;
          if (pendingOperations <= 0) {
            this.isInitializing = false;
            console.log('Initialization complete');
          }
        };
  
        this.roleService.initialize()
          .pipe(take(1), finalize(() => markComplete()))
          .subscribe(roles => {
            if (roles) {
              this.updateRoles(roles);
            }
          });
  
        this.identityService.initialize()
          .pipe(take(1), finalize(() => markComplete()))
          .subscribe(providers => {
            if (providers) {
              this.updateProviders(providers);
            }
          });
  
        this.licenceGroupService.initialize()
          .pipe(take(1), finalize(() => markComplete()))
          .subscribe(licenceGroups => {
            if (licenceGroups) {
              this.updateLicenceGroupDetails(licenceGroups);
            }
          });
  
        this.userGroupService.initialize()
          .pipe(take(1), finalize(() => markComplete()))
          .subscribe(userGroups => {
            if (userGroups) {
              this.updateUserGroupDetails(userGroups);
            }
          });
  
        this.licenceService.initialize()
          .pipe(take(1), finalize(() => markComplete()))
          .subscribe(licence => {
            if (licence) {
              this.updateLicenceDetails(licence);
            }
          });
  
        this.userService.initialize()
          .pipe(take(1), finalize(() => markComplete()))
          .subscribe(users => {
            if (users) {
              this.updateUserDetails(users);
            }
          });
  
        this.loadConfigurationSummaries();
      } else {
        this.isInitializing = false;
      }
    });
  }


  loadConfigurationSummaries() {
    if (this.configRequestInProgress) {
      console.log('Configuration request already in progress, skipping duplicate call');
      return;
    }
    
    this.configRequestInProgress = true;
    
    this.configurationService.getAllConfigurationSummaries()
      .pipe(
        take(1),
        finalize(() => {
          this.configRequestInProgress = false;
        })
      )
      .subscribe(summaries => {
        if (summaries) {
          this.updateConfigurationSummaries(summaries);
        }
      });
  }

  /**
   * Sets the current selected configuration details and loads its versions
   * @param configId The ID of the configuration to set as current
   * @returns void
   */
  setCurrentSelectedConfigurationDetails(configId: string) {
    const existingConfig = this.dataStore.configurationSummaries.find(c => c.configurationId === configId);

    if (existingConfig) {
      this.dataStore.currentSelectedConfigurationDetail = { ...existingConfig };
      this._currentSelectedConfigurationDetails.next({ ...existingConfig });
      console.log('Using cached config:', existingConfig);
      this.loadConfigurationVersions(configId);
    } else {
      this.configurationService.getConfigurationById(configId)
        .pipe(take(1))
        .subscribe(config => {
          if (config) {
            const configSummary: ConfigurationSummary = {
              configurationId: config.configurationId,
              configurationName: config.configurationName,
              configurationVersion: config.configurationVersion,
              configurationSchemaVersion: config.configurationSchemaVersion,
              publishedBy: config.publishedBy,
              modifiedDate: config.modifiedDate,
            };

            console.log('Loaded config from service:', configSummary);

            this.dataStore.currentSelectedConfigurationDetail = configSummary;
            this._currentSelectedConfigurationDetails.next({ ...this.dataStore.currentSelectedConfigurationDetail });

            this.loadConfigurationVersions(configId);
          }
        });
    }
  }

  private loadConfigurationVersions(configId: string) {
    this.configurationService.getAllConfigurationVersions(configId)
      .pipe(
        take(1),
        catchError(error => {
          console.error('Error loading configuration versions:', error);
          return of([]);
        })
      )
      .subscribe(versions => {
        this.dataStore.currentConfigurationVersions = versions || [];
        this._currentConfigurationVersions.next([...this.dataStore.currentConfigurationVersions]);
      });
  }

  private updateConfigurationSummaries(summaries: ConfigurationSummary[]) {
    this.dataStore.configurationSummaries = summaries;
    this._configurationSummaries.next([...this.dataStore.configurationSummaries]);
  }

  addConfiguration(config: Configuration): Observable<any> {
    return this.configurationService.addConfiguration(config)
      .pipe(map(response => {
        this.loadConfigurationSummaries();
        return response;
      }));
  }

  updateConfiguration(config: Configuration): Observable<any> {
    return this.configurationService.updateConfiguration(config.configurationId, config)
      .pipe(map(response => {
        this.loadConfigurationSummaries();
        if (this.dataStore.currentSelectedConfigurationDetail &&
          this.dataStore.currentSelectedConfigurationDetail.configurationId === config.configurationId) {
          this.setCurrentSelectedConfigurationDetails(config.configurationId);
        }
        return response;
      }));
  }

  deleteConfiguration(configId: string): Observable<any> {
    return this.configurationService.deleteConfiguration(configId)
      .pipe(map(response => {
        this.loadConfigurationSummaries();
        if (this.dataStore.currentSelectedConfigurationDetail &&
          this.dataStore.currentSelectedConfigurationDetail.configurationId === configId) {
          this._currentSelectedConfigurationDetails.next(null);
          this._currentConfigurationVersions.next([]);
        }
        return response;
      }));
  }

  // THE FIXED METHOD - Key part of the solution
  setCurrentSelectedLicenceGroupDetails(group: LicenceGroup) {
    this.dataStore.currentSelectedLicenceGroupDetail = group;
    this._currentSelectedLicenceGroupDetail.next(group ? { ...group } : null);

    if (group) {
      this.userService.getAllActiveUserData()
        .pipe(take(1))
        .subscribe(users => {
          if (users && Array.isArray(users)) {
            const licenceGroupUsers = users.filter(user =>
              user.licenceGroups &&
              Array.isArray(user.licenceGroups) &&
              user.licenceGroups.some(g => g.id === group.id)
            );

            this.dataStore.currentLicenceGroupUsers = licenceGroupUsers;

            this._currentLicenceGroupUsers.next([...licenceGroupUsers]);
          } else {
            this._currentLicenceGroupUsers.next([]);
          }
        });

      const licenceUserGroups = this.getLicenceUserGroups(group.id);

      this.dataStore.currentLicenceUserGroups = licenceUserGroups;

      this._currentLicenceUserGroups.next([...licenceUserGroups]);
    } else {
      this._currentLicenceGroupUsers.next([]);
      this._currentLicenceUserGroups.next([]);
    }
  }

  // FIXED METHOD - This is part of the solution
  private getLicenceUserGroups(groupId: number): UserGroup[] {
    const filteredUserGroupArray: UserGroup[] = [];

    if (this.dataStore.userGroupDetails && Array.isArray(this.dataStore.userGroupDetails)) {
      this.dataStore.userGroupDetails.forEach(userGroup => {
        if (userGroup.licenceGroups &&
          Array.isArray(userGroup.licenceGroups) &&
          userGroup.licenceGroups.some(group => group.id === groupId)) {
          filteredUserGroupArray.push({ ...userGroup });
        }
      });
    }

    return filteredUserGroupArray;
  }

  /**
   * Explicitly loads users for a specific licence group
   * @param licenceGroupId The ID of the licence group to load users for
   */
  loadLicenceGroupUsers(licenceGroupId: number) {
    if (!licenceGroupId) {
      this._currentLicenceGroupUsers.next([]);
      return;
    }

    this.userService.getAllActiveUserData()
      .pipe(take(1))
      .subscribe(users => {
        if (users && Array.isArray(users)) {
          const licenceGroupUsers = users.filter(user =>
            user.licenceGroups &&
            Array.isArray(user.licenceGroups) &&
            user.licenceGroups.some(g => g.id === licenceGroupId)
          );

          this.dataStore.currentLicenceGroupUsers = licenceGroupUsers;
          this._currentLicenceGroupUsers.next([...licenceGroupUsers]);
        } else {
          this._currentLicenceGroupUsers.next([]);
          console.log('No users found or invalid data received');
        }
      });
  }

  setCurrentSelectedUserGroupDetails(group: UserGroup) {
    this.dataStore.currentSelectedUserGroupDetail = group;
    this._currentSelectedUserGroupDetail.next(group ? { ...group } : null);

    if (group) {
      this.getUserGroupUsers(group.id).pipe(take(1)).subscribe(users => {
        this.dataStore.currentUserGroupUsers = users;
        this._currentUserGroupUsers.next(users ? [...users] : []);
      });

      const userGroupLicenceGroups = this.getUserGroupLicenceGroups(group);
      this.dataStore.currentUserGroupLicenceGroups = userGroupLicenceGroups;
      this._currentUserGroupLicenceGroups.next(userGroupLicenceGroups ? [...userGroupLicenceGroups] : []);
    } else {
      this._currentUserGroupUsers.next([]);
      this._currentUserGroupLicenceGroups.next([]);
    }
  }

  setCurrentSelectedUserDetails(user: UserDetailData) {
    if (user) {
      const provider = this.getProvider(user.directoryId);
      this.dataStore.currentProvider = provider;
      this._currentProvider.next(provider ? { ...provider } : null);

      this.dataStore.currentSelectedUserDetail = user;
      this._currentSelectedUserDetail.next({ ...user });

      const userLicenceGroups = this.getLicenceGroups(user);
      this.dataStore.currentUserLicenceGroups = userLicenceGroups;
      this._currentUserLicenceGroups.next(userLicenceGroups ? [...userLicenceGroups] : []);

      const userUserGroups = this.getUserUserGroups(user);
      this.dataStore.currentUserUserGroups = userUserGroups;
      this._currentUserUserGroups.next(userUserGroups ? [...userUserGroups] : []);
    } else {
      this._currentProvider.next(null);
      this._currentSelectedUserDetail.next(null);
      this._currentUserLicenceGroups.next([]);
      this._currentUserUserGroups.next([]);
    }
  }

  private updateRoles(roles: Role[]) {
    this.dataStore.roles = roles;
    this._roles.next(roles ? [...roles] : []);
  }

  private updateProviders(providers: Provider[]) {
    this.dataStore.providers = providers;
    this._providers.next(providers ? [...providers] : []);
  }

  private updateLicenceDetails(licence: Licence) {
    this.dataStore.licence = licence;
    this._licence.next(licence ? { ...licence } : null);
  }

  private updateLicenceGroupDetails(details: LicenceGroup[]) {
    const previouslySelected: LicenceGroup = this.dataStore.currentSelectedLicenceGroupDetail;

    this.dataStore.licenceGroupDetails = details;
    this._licenceGroupDetails.next(details ? [...details] : []);

    if (this.dataStore.currentSelectedLicenceGroupDetail != null && details != null) {
      const currentSelected = details.find(x => x.id === this.dataStore.currentSelectedLicenceGroupDetail.id);
      if (previouslySelected && currentSelected) {
        currentSelected.users = previouslySelected.users;  // TODO: Workaround for server not having...change it
      }
      this.setCurrentSelectedLicenceGroupDetails(currentSelected);
    }
  }

  private updateUserGroupDetails(userGroups: UserGroup[]) {
    const previouslySelected: UserGroup = this.dataStore.currentSelectedUserGroupDetail;
    if (userGroups && Array.isArray(userGroups)) {
      const groupsCopy = [...userGroups];
      groupsCopy.forEach(group => {
        this.getUserGroupUsers(group.id).pipe(take(1)).subscribe(users => {
          group.users = users || [];
        });
      });

      this.dataStore.userGroupDetails = groupsCopy;
      this._userGroupDetails.next([...groupsCopy]);

      if (this.dataStore.currentSelectedUserGroupDetail != null) {
        const currentSelected = groupsCopy.find(x => x.id === this.dataStore.currentSelectedUserGroupDetail.id);
        if (previouslySelected && currentSelected) {
          currentSelected.id = previouslySelected.id;
          currentSelected.users = previouslySelected.users;
        }
        this.setCurrentSelectedUserGroupDetails(currentSelected);
      }
    } else {
      this.dataStore.userGroupDetails = [];
      this._userGroupDetails.next([]);
    }
  }

  private updateUserDetails(details: UserDetailData[]) {
    this.dataStore.userDetails = details;
    this._userDetails.next(details ? [...details] : []);

    if (this.dataStore.currentSelectedUserDetail != null && details != null) {
      const updatedUser = details.find(x => x.emailAddress === this.dataStore.currentSelectedUserDetail.emailAddress);
      this.setCurrentSelectedUserDetails(updatedUser);
    }
  }

  getProvider(id: number): Provider {
    if (this.dataStore.providers && Array.isArray(this.dataStore.providers)) {
      return this.dataStore.providers.find(p => p.id === id);
    }
    return null;
  }

  private getLicenceGroups(user: UserDetailData): LicenceGroup[] {
    const filteredUserLicenceGroupArray: LicenceGroup[] = [];
    if (this.dataStore.licenceGroupDetails && Array.isArray(this.dataStore.licenceGroupDetails)) {
      this.dataStore.licenceGroupDetails.forEach(licenceGroup => {
        if (user.licenceGroups &&
          Array.isArray(user.licenceGroups) &&
          user.licenceGroups.some(group => group.id === licenceGroup.id)) {
          filteredUserLicenceGroupArray.push({ ...licenceGroup });
        }
      });
    }
    return filteredUserLicenceGroupArray;
  }

  private getUserUserGroups(user: UserDetailData): UserGroup[] {
    const filteredUserGroupArray: UserGroup[] = [];
    if (this.dataStore.userGroupDetails && Array.isArray(this.dataStore.userGroupDetails)) {
      this.dataStore.userGroupDetails.forEach(group => {
        if (group.users &&
          Array.isArray(group.users) &&
          group.users.some(groupUser => groupUser.emailAddress === user.emailAddress)) {
          filteredUserGroupArray.push({ ...group });
        }
      });
    }
    return filteredUserGroupArray;
  }

  private getUserGroupLicenceGroups(userGroup: UserGroup): LicenceGroup[] {
    const filteredGroupLicenceArray: LicenceGroup[] = [];
    if (this.dataStore.licenceGroupDetails &&
      Array.isArray(this.dataStore.licenceGroupDetails) &&
      userGroup.licenceGroups &&
      Array.isArray(userGroup.licenceGroups)) {
      this.dataStore.licenceGroupDetails.forEach(licenceGroup => {
        if (userGroup.licenceGroups.some(group => group.id === licenceGroup.id)) {
          filteredGroupLicenceArray.push({ ...licenceGroup });
        }
      });
    }
    return filteredGroupLicenceArray;
  }


  /*TODO: these are used to keep data in sync...in
  a lot of cases modifying in the store would be better
  than hitting server.  It is a shortcut and should dbe worked out.*/

  refreshUserActivity() {
    this.refreshUserDetails();
    this.refreshLicenceGroupDetails();
  }

  refreshLicenceState() {
    this.authService.hasPermission('groups:*').then(hasPermission => {
      if (hasPermission) {
        this.refreshUserDetails();
        this.refreshLicenceGroupDetails();
        this.refreshLicence();
      }
    });
  }

  refreshConfigurationState() {
    this.loadConfigurationSummaries();
    if (this.dataStore.currentSelectedConfigurationDetail) {
      this.setCurrentSelectedConfigurationDetails(this.dataStore.currentSelectedConfigurationDetail.configurationId);
    }
  }

  private refreshUserGroupDetails() {
    this.userGroupService.getUserGroups().subscribe((data) => {
      this.updateUserGroupDetails(data);
    });
  }

  private refreshUserDetails() {
    this.userService.getAllActiveUserData().subscribe((data) => {
      this.updateUserDetails(data);
    });
  }

  private refreshLicenceGroupDetails() {
    this.licenceService.getLicenceGroups().subscribe((data) => {
      this.updateLicenceGroupDetails(data);
    });
  }

  private refreshLicence() {
    this.licenceService.getCurrentLicence().subscribe((licence) => {
      this.updateLicenceDetails(licence);
    });
  }

  private refreshProviders() {
    this.identityService.getAvailableProviders().subscribe((data) => {
      this.updateProviders(data);
    });
  }

  private getUserGroupUsers(id: number): Observable<UserDetailData[]> {
    return this.userGroupService.getUserGroupUsers(id);
  }

  get licenceGroupDetails() {
    return this._licenceGroupDetails.asObservable();
  }

  get licence() {
    return this._licence.asObservable();
  }

  get roles() {
    return this._roles.asObservable();
  }

  get providers() {
    return this._providers.asObservable();
  }

  get userDetails() {
    return this._userDetails.asObservable();
  }

  get currentSelectedLicenceGroupDetails() {
    return this._currentSelectedLicenceGroupDetail.asObservable();
  }

  get currentUserGroupUsers() {
    return this._currentUserGroupUsers.asObservable();
  }

  get currentLicenceGroupUsers() {
    return this._currentLicenceGroupUsers.asObservable();
  }

  get currentLicenceUserGroups() {
    return this._currentLicenceUserGroups.asObservable();
  }

  get currentSelectedUserDetails() {
    return this._currentSelectedUserDetail.asObservable();
  }

  get currentUserProvider() {
    return this._currentProvider.asObservable();
  }

  get currentUserLicenceGroups() {
    return this._currentUserLicenceGroups.asObservable();
  }

  get currentUserGroupLicenceGroups() {
    return this._currentUserGroupLicenceGroups.asObservable();
  }

  get currentUserUserGroups() {
    return this._currentUserUserGroups.asObservable();
  }

  get userGroupDetails() {
    return this._userGroupDetails.asObservable();
  }

  get currentSelectedUserGroupDetails() {
    return this._currentSelectedUserGroupDetail.asObservable();
  }

  get currentSelectedConfigurationDetails() {
    return this._currentSelectedConfigurationDetails.asObservable();
  }

  get currentConfigurationVersions() {
    return this._currentConfigurationVersions.asObservable();
  }

  get configurationSummaries() {
    return this._configurationSummaries.asObservable();
  }

  activate(activationFile: FileInput): Observable<any> {
    return this.licenceService.activateWithLicenceFile(activationFile)
      .pipe(finalize(() => {
        this.refreshLicenceState();
      }));
  }

  addLicenceGroup(group: LicenceGroup): Observable<any> {
    return this.licenceService.addLicenceGroup(group).pipe(map(response => {
      this.refreshLicenceGroupDetails();
      return response;
    }));
  }

  addUser(newUser: User): Observable<any> {
    return this.userService.addUser(newUser)
      .pipe(map(result => {
        this.refreshUserDetails();
        return result;
      }));
  }

  addAdminUser(userName: string, password: string): Observable<any> {
    const input = new FormData();
    input.append('adminPassword', password);
    input.append('adminPasswordConfirm', password);
    input.append('adminName', userName);
    return this.userService.addAdminUser(input);
  }

  addUserGroup(group: UserGroup): Observable<number> {
    return this.userGroupService.addUserGroup(group)
      .pipe(map(id => {
        this.refreshUserGroupDetails();

        this.userGroupDetails
          .pipe(take(1))
          .subscribe(() => {
            this.refreshUserDetails();
          });

        return id;
      }));
  }

  connectToProvider(provider: Provider): Observable<Provider> {
    return this.identityService.attemptToChangeProvider(provider).pipe(map(responseDirectory => {
      this.refreshProviders();
      return provider;
    }));

  }

  deleteProvider(id: number) {
    if (id) {
      this.identityService.deleteProvider(id).subscribe(() => {
        this.refreshProviders();
        this.refreshUserGroupDetails();
        this.refreshUserDetails();
        this.refreshLicenceGroupDetails();
      });
    }
  }


  deleteUserGroup(group: UserGroup): Observable<any> {
    return this.userGroupService.deleteUserGroup(group)
      .pipe(map(res => {
        this.refreshUserDetails();
        this.refreshUserGroupDetails();
        this.refreshLicenceGroupDetails();
        return res;
      }));
  }

  deleteLicenceGroup(group: LicenceGroup): Observable<any> {
    return this.licenceService.deleteLicenceGroup(group)
      .pipe(map(response => {
        this.refreshUserGroupDetails();
        this.refreshUserDetails();
        this.refreshLicenceGroupDetails();
        return response;
      }));
  }


  deleteUser(userDetail: UserDetailData) {
    this.userService.deleteUser(userDetail.userId)
      .subscribe(() => {
        this.refreshUserDetails();
        this.refreshLicenceGroupDetails();
        this.refreshUserGroupDetails();
        this._userDeleted.next(true);
      });
  }

  removeUserSession(userDetail: UserDetailData) {
    this.userService.removeUserSession(userDetail).subscribe(details => {
      this.userService.getAllActiveUserData().subscribe(data => {
        this.updateUserDetails(data);
      });
      this.licenceService.getLicenceGroups().subscribe(data => {
        this.updateLicenceGroupDetails(data);
      });
    });
  }

  updateUsers(users: UserDetailData[]) {
    users.forEach(userDetail => {
      const user = this.userService.getUser(userDetail);
      this.userService.editUser(user)
        .pipe(map(response => {
          this.refreshUserDetails();
          this.refreshLicenceGroupDetails();
          return response;
        })).subscribe();
    });
  }


  updateUserGroups(groups: UserGroup[]) {
    this.userGroupService.updateUserGroups(groups)
      .subscribe(
        () => {
          this.refreshUserGroupDetails();
          this.refreshLicenceGroupDetails();
          this.refreshUserDetails();
        },
        error => {
          console.error(error);
        });
  }

  updateLicenceGroups(groups: LicenceGroup[]) {
    this.licenceService.updateLicenceGroups(groups)
      .subscribe(_ => {
        this.refreshUserGroupDetails();
        this.refreshUserDetails();
        this.refreshLicenceGroupDetails();
      },
        error => {
          console.error(error);
        });
  }

  syncUserGroup(group: UserGroup) {
    this.activeDirectoryService.syncUserGroup(group)
      .pipe(map(_ => {
        this.refreshUserDetails();
        this.refreshUserGroupDetails();
        this.refreshLicenceGroupDetails();
        this.setCurrentSelectedUserGroupDetails(group);
      })).subscribe();
  }

  /**
   * Sets the userDeleted observable to true or false
  */
  get userDeleted() {
    return this._userDeleted.asObservable();
  }

  /**
 * Gets a user by ID from the dataStore
 * @param userId User ID to look up
 * @returns UserDetailData object or null if not found
 */
  getUserById(userId: number): UserDetailData {
    if (!this.dataStore.userDetails || this.dataStore.userDetails.length === 0) {
      return null;
    }

    return this.dataStore.userDetails.find(user => user.userId === userId);
  }

  /**
   * Gets a user group by ID from the dataStore
   * @param groupId User group ID to look up
   * @returns UserGroup object or null if not found
   */
  getUserGroupById(groupId: number): UserGroup {
    if (!this.dataStore.userGroupDetails || this.dataStore.userGroupDetails.length === 0) {
      return null;
    }

    return this.dataStore.userGroupDetails.find(group => group.id === groupId);
  }

  /**
   * Gets a configuration by ID from the dataStore
   * @param configId Configuration ID to look up
   * @returns ConfigurationSummary object or null if not found
   */
  getConfigurationById(configId: string): ConfigurationSummary {
    if (!this.dataStore.configurationSummaries || this.dataStore.configurationSummaries.length === 0) {
      return null;
    }

    return this.dataStore.configurationSummaries.find(config => config.configurationId === configId);
  }
}